import { useMemo, useState } from 'react'
import { StructuredDataSection } from '@/components/document-overlay/StructuredDataSection'
import { Button } from '@/components/ui/button'
import {
  DocumentTableProcessing,
  type TableProcessingDocument,
} from '@/features/documents/DocumentTableProcessing'
import { isTableFilename } from '@/features/documents/table-processing'
import { type DocumentMapping, mappingsByDocument } from '@/services/api/structuredIngest'
import { isTableFileType } from '@/utils/table-files'
import {
  useAnalyzeStructuredDocument,
  useStructuredIngestAnalysis,
  useStructuredIngestList,
  useStructuredIngestReadiness,
} from './hooks/use-structured-ingest'
import { SchemaRequiredCard } from './SchemaRequiredCard'
import { type RowsTarget, StructuredRowsDialog } from './StructuredRowsDialog'

/**
 * The document overlay's view of a tabular file's structured ingest: the newest
 * analysis of this document (from the case list, which the documents page keeps
 * warm), the saved processing choice, and the rows drilldown.
 */
export function DocumentStructuredData({
  caseId,
  document,
}: {
  caseId: string
  document: TableProcessingDocument
}) {
  const documentId = document.id
  const list = useStructuredIngestList(caseId)
  const mapping = useMemo(
    () => mappingsByDocument(list.data ?? [])[documentId],
    [list.data, documentId]
  )
  const wholeTableAi = document.tabular_extraction_mode === 'llm'
  return (
    <>
      {(mapping || isTableFilename(document.filename) || wholeTableAi) && (
        <DocumentTableProcessing caseId={caseId} document={document} />
      )}
      {!wholeTableAi && !mapping && list.isSuccess && (
        <PrepareMapping caseId={caseId} document={document} />
      )}
      {!wholeTableAi && mapping && <MappingDetails caseId={caseId} mapping={mapping} />}
    </>
  )
}

function MappingDetails({ caseId, mapping }: { caseId: string; mapping: DocumentMapping }) {
  const analysis = useStructuredIngestAnalysis(caseId, mapping.analysisId)
  const [rowsTarget, setRowsTarget] = useState<RowsTarget | null>(null)
  return (
    <>
      <StructuredDataSection
        state={mapping.state}
        analysis={analysis.data ?? null}
        reviewHref={`/cases/${caseId}/structured-ingest?analysis=${encodeURIComponent(mapping.analysisId)}`}
        onShowRows={(tableId, name) =>
          setRowsTarget({ analysisId: mapping.analysisId, tableId, query: {}, title: name })
        }
      />
      <StructuredRowsDialog
        caseId={caseId}
        target={rowsTarget}
        onClose={() => setRowsTarget(null)}
      />
    </>
  )
}

function PrepareMapping({
  caseId,
  document,
}: {
  caseId: string
  document: { id: string; filename: string }
}) {
  const prepare = useAnalyzeStructuredDocument(caseId)
  const readiness = useStructuredIngestReadiness(caseId)
  const extension = document.filename.split('.').pop()?.toLowerCase() ?? ''
  const text = ['txt', 'log'].includes(extension)
  if (!text && !isTableFileType(extension)) return null
  if (!readiness.data?.ready)
    return readiness.isSuccess ? <SchemaRequiredCard caseId={caseId} /> : null
  return (
    <section className="space-y-2 rounded-lg border border-border bg-muted/40 p-3">
      <p className="text-sm font-medium">{text ? 'Tabular data' : 'Structured mapping required'}</p>
      <p className="text-xs text-muted-foreground">
        {text
          ? 'Check whether this file contains rows that need a structured mapping.'
          : 'Prepare and confirm a mapping to extract this file. Its rows are preserved in full.'}
      </p>
      <Button
        size="sm"
        variant="outline"
        loading={prepare.isPending}
        onClick={() => prepare.mutate({ documentId: document.id })}
      >
        {text ? 'Check for tabular data' : 'Prepare mapping'}
      </Button>
      {prepare.error && (
        <p role="alert" className="text-xs text-destructive">
          {prepare.error.message}
        </p>
      )}
    </section>
  )
}
